<script lang="ts">
import { defineComponent as __MACROS_defineComponent } from "vue";
export default /*#__PURE__*/ __MACROS_defineComponent({
  name: 'StatusTag',
});
</script>
<script setup lang="ts">
import { computed } from 'vue';
import { transformI18n } from '@klondpay/locales';

/**
 * 状态标签组件 (StatusTag)
 * 支持普通标签样式和带状态点的文本描述样式
 */
interface Props {
  /** 样式类型: primary / success / info / warning / danger */
  type?: 'primary' | 'success' | 'info' | 'warning' | 'danger';
  /** 直接显示的文本 */
  text?: string;
  /** 数据源 (兼容旧版名称): 可以是原始状态字符串数字，或者是整个数据对象 */
  status?: string | number | Record<string, unknown> | unknown;
  /** 数据源: 可以是原始状态字符串数字，或者是整个数据对象 */
  data?: string | number | Record<string, unknown> | unknown;
  /** 当 data 为 Object 时，指定状态取值的字段名 */
  statusField?: string;
  /** 状态映射表，支持格式为 [[value, { text, type }]] 的数组 */
  statusMap?: Array<[unknown, { text?: string; type?: string }]>;
  /** 是否需要国际化，默认 true */
  i18n?: boolean;
  /** 空数据展示文本 */
  emptyText?: string;
  /** 自定义颜色：point 模式下为圆点颜色，dark 模式下为字体颜色 */
  color?: string;
  /** 自定义背景颜色 */
  bgColor?: string;
  /** 样式效果: 'dark' (标签形) / 'point' (圆点形)，默认 'point' */
  effect?: 'dark' | 'point';
}

;

const props = withDefaults(defineProps<Props>(), {
  i18n: true,
  effect: 'point',
  emptyText: '--',
});

/**
 * 计算最终显示的状态详情
 */
const statusDetail = computed(() => {
  let val: unknown = undefined;

  // 1. 提取原始值 (优先用 data, data 没有用 status)
  const sourceData = props.data !== undefined ? props.data : props.status;

  if (sourceData !== undefined && sourceData !== null) {
    if (typeof sourceData === 'object' && props.statusField) {
      val = (sourceData as Record<string, any>)[props.statusField];
    } else {
      val = sourceData;
    }
  }

  let resText = props.text;
  let resType = props.type;

  // 2. 根据状态映射表进行查找
  if (props.statusMap && val !== undefined) {
    // 兼容 Map 构造风格的数组映射: [[key, {text, type}]]
    const mapEntry = props.statusMap.find((item) => Array.isArray(item) && item[0] === val);
    if (mapEntry && mapEntry[1]) {
      resText = mapEntry[1].text || resText;
      resType = mapEntry[1].type || resType;
    } else {
      // 兼容对象映射或简单值 fallback
      const simpleMap = props.statusMap.find((item) => item.value === val);
      if (simpleMap) {
        resText = simpleMap.text || resText;
        resType = simpleMap.type || resType;
      }
    }
  }

  // 3. Fallback 到 val 自身 (如果 resText 为空)
  if (!resText && val !== undefined && val !== null) {
    resText = String(val);
  }

  // 4. 空文本处理
  if (!resText) {
    resText = props.emptyText;
  }

  // 5. 国际化处理
  if (props.i18n && resText && resText !== props.emptyText) {
    resText = transformI18n(resText);
  }

  return {
    text: resText,
    type: resType,
  };
});

/**
 * 圆点样式类名映射
 */
const pointClass = computed(() => {
  const type = statusDetail.value.type;
  if (!type) return 'is-info';

  // 如果传了自定义 color，不使用预设类名
  if (props.color) return '';

  const map: Record<string, string> = {
    primary: 'is-primary',
    danger: 'is-danger',
    success: 'is-success',
    warning: 'is-warning',
    info: 'is-info',
    // 兼容旧数字映射
    '0': 'is-danger',
    '1': 'is-success',
    '2': 'is-warning',
  };

  return map[type] || 'is-info';
});

/**
 * 圆点自定义颜色样式（通过 color 属性自定义圆点颜色）
 */
const pointCustomStyle = computed(() => {
  if (props.color) {
    return { backgroundColor: props.color };
  }
  return {};
});

/**
 * 自定义样式
 */
const customStyle = computed(() => {
  const style: Record<string, string> = {};

  // point 模式下 color 控制圆点颜色，不设置字体颜色
  // dark 模式下 color 控制字体颜色
  if (props.color && props.effect !== 'point') {
    style.color = props.color;
  }
  if (props.bgColor) style.backgroundColor = props.bgColor;
  return style;
});
</script>

<template>
  <!-- 圆点式样 (point) -->
  <div v-if="effect === 'point'" class="oper-status-box" :style="customStyle">
    <div class="status-point" :class="pointClass" :style="pointCustomStyle" />
    <div class="statusText">
      <slot>{{ statusDetail.text }}</slot>
    </div>
  </div>

  <!-- 标签式样 (dark) -->
  <el-tag
    v-else
    :type="statusDetail.type as any"
    :effect="effect === 'dark' ? 'dark' : 'plain'"
    class="status-tag-rect"
    :style="customStyle"
  >
    <slot>{{ statusDetail.text }}</slot>
  </el-tag>
</template>

<style scoped lang="scss">
.oper-status-box {
  display: flex;
  align-items: center;
  justify-content: center;

  .statusText {
    margin-left: 8px;
  }
}

/* 状态圆点基础样式 */
.status-point {
  width: 6px;
  height: 6px;
  border-radius: 50%;

  &.is-primary {
    background-color: #039fff;
  }

  &.is-success {
    background-color: #71c241;
  }

  &.is-warning {
    background-color: #efb144;
  }

  &.is-danger {
    background-color: #ea5d56;
  }

  &.is-info {
    background-color: #d5d5d5;
  }
}

.status-tag-rect {
  border: none;
}
</style>
